import { useEffect, useRef, useState } from "react";
import getRecommendations from "../../api/mightLikeApi";
import { useIsInStockContext } from "../../context/isInStock/IsInStockContext";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { useStockPrice } from "../../context/stockPrice/StockPriceContext";
import {
  isProductDiscontinued,
  isProductUnavailableForUS,
} from "../../../utils/productUtils";

import {
  matchAdvertsToMightLikeProducts,
  getVariantIdCollection,
  buildChildToParentIdMap,
} from "../../../common/CriteoAdvertsForRecs";
import criteoApi from "../../api/criteoApi";
import getProductSummaries from "../../api/productSummariesApi";
import useAdverts from "../../../client/hooks/useAdverts";
import { useCriteoBanner } from "../../context/criteoBanner/CriteoBannerContext";
import { useFeaturesReady } from "../../context/featuresReady/FeaturesReadyContext";

import { logError, ERROR_TYPE, logEvent } from "../../utils/newRelicLogger";
import { YOU_MIGHT_LIKE_SECTION_ADS_CONFIG } from "../../constants";
import { isCriteoBannerBucket } from "../../utils/criteoAdsBucket";

const hasCriteoResponse = (data) =>
  Array.isArray(data?.products) && data.products.length > 0;

export default function useRecommendations(
  { productId, scope, pageArea },
  hasCriteoAdverts = false,
) {
  const { isStockStateReady, isInStock } = useIsInStockContext();
  const { areFeaturesReady } = useFeaturesReady();
  const { isMixAndMatch, product, storeCode } = useRuntimeConstants();
  const isDiscontinued = isProductDiscontinued(product);
  const isUnavailableForUS = isProductUnavailableForUS(product, storeCode);
  const [mightLikeProducts, setMightLikeProducts] = useState(null);
  const [recsRequestId, setRecsRequestId] = useState("");
  const [hasFetchedRecs, setHasFetchedRecs] = useState(false);
  const [apiFailed, setApiFailed] = useState(false);
  const { advertsSdkInstance } = useAdverts();
  const [mightLikeProductsWithAdverts, setMightLikeProductsWithAdverts] =
    useState(null);
  const [hasCriteoAds, setHasCriteoAds] = useState(false);
  const { products: productPricing } = useStockPrice();
  const { setBannerData } = useCriteoBanner();
  const productPrice = productPricing?.price;
  const previousProductIdRef = useRef(productId);

  const getVariantIds = (products, productsWithVariantIdsCollection) => {
    try {
      return getVariantIdCollection(products, productsWithVariantIdsCollection);
    } catch (variantError) {
      logError(ERROR_TYPE.NonCriticalAppError, {
        message: variantError.message,
        action: "GetVariantIdCollection",
      });
      return [];
    }
  };

  useEffect(() => {
    if (isMixAndMatch || !isStockStateReady || !productId) {
      return;
    }

    let isCancelled = false;

    const safeStateSetter = (setter, value) => {
      if (!isCancelled) {
        setter(value);
      }
    };

    const setAdvertsFallback = (products = []) => {
      const fallbackProducts = products || [];

      safeStateSetter(setMightLikeProducts, fallbackProducts);
      safeStateSetter(setMightLikeProductsWithAdverts, fallbackProducts);
      safeStateSetter(setHasCriteoAds, false);
    };

    const fetchRecommendations = async () => {
      try {
        const response = await getRecommendations({
          isInStock,
          isDiscontinued,
          isUnavailableForUS,
          scope,
          productId,
          pageArea,
          handleErrorReturnValue: () => {
            safeStateSetter(setApiFailed, true);
          },
        });
        const products = response?.recommendations;

        safeStateSetter(setMightLikeProducts, products);
        safeStateSetter(setRecsRequestId, response?.recsRequestId ?? "");
        safeStateSetter(setHasFetchedRecs, true);

        if (!hasCriteoAdverts) {
          setAdvertsFallback(products);
          return;
        }

        // Wait for Optimizely so isCriteoBannerBucket() below reflects the real
        // bucket, not the "not enabled yet" default: falling back here (rather
        // than at the top of the effect) means non-Criteo callers of this hook
        // are unaffected, and this re-runs once features do become ready
        // (areFeaturesReady is an effect dependency) instead of getting stuck
        // with a stale bucket for the rest of the page view.
        if (!areFeaturesReady) {
          setAdvertsFallback(products);
          return;
        }

        const isBannerBucket = isCriteoBannerBucket();

        if (!products?.length) {
          setAdvertsFallback();
          if (!isBannerBucket) {
            return;
          }
        }

        let variantIds = [];
        let childToParentIdMap = new Map();

        if (products?.length) {
          try {
            const productsWithVariantIdsCollection =
              await getProductSummaries(products);

            variantIds = getVariantIds(
              products,
              productsWithVariantIdsCollection,
            );
            childToParentIdMap = buildChildToParentIdMap(
              productsWithVariantIdsCollection,
            );
          } catch (summariesError) {
            logError(ERROR_TYPE.NonCriticalAppError, {
              message: summariesError.message,
              action: "GetProductSummaries",
            });
          }
        }

        if (!variantIds?.length && !isBannerBucket) {
          setAdvertsFallback(products);
          return;
        }

        const criteoAdData = await criteoApi({
          productIdCollection: variantIds,
          productId,
          isInStock,
          price: productPrice,
          advertsSdkInstance,
          adsConfig: YOU_MIGHT_LIKE_SECTION_ADS_CONFIG,
          isCriteoBannerBucket: isBannerBucket,
        });

        if (!isCancelled && setBannerData && isBannerBucket) {
          setBannerData(criteoAdData?.banner);
        }

        if (!hasCriteoResponse(criteoAdData)) {
          setAdvertsFallback(products);
          return;
        }

        const { OnLoadBeacon, OnViewBeacon, responseId } = criteoAdData;
        const containerBeacons = {
          OnLoadBeacon,
          OnViewBeacon,
          responseId,
        };

        const productsWithAdverts = matchAdvertsToMightLikeProducts(
          products,
          criteoAdData,
          containerBeacons,
          YOU_MIGHT_LIKE_SECTION_ADS_CONFIG,
          childToParentIdMap,
        );

        const hasMatchedSponsoredProducts = Boolean(
          productsWithAdverts?.some((productItem) => productItem.advertisement),
        );

        // Criteo painted-rate diagnostics: compare how many sponsored products
        // Criteo returned against how many we actually matched to a tile (and
        // therefore paint + fire the OnLoadBeacon for). A large gap here means
        // Criteo covered ads we never render, which is what drives the painted
        // rate down even though manual spot-checks look fine. Emitted as a
        // NewRelic page action so it can be aggregated across real sessions.
        const sponsoredProductsReturned = Array.isArray(criteoAdData?.products)
          ? criteoAdData.products.length
          : 0;
        // Count UNIQUE matched adverts by advertisement ProductId. A single
        // advert can be attached to multiple tiles when recommendations contain
        // duplicate productIds, so counting tiles could report more matches than
        // Criteo returned (and a negative unmatched count). Deduping keeps
        // matched <= returned and unmatched >= 0.
        const matchedAdvertIds = Array.isArray(productsWithAdverts)
          ? new Set(
              productsWithAdverts
                .filter((productItem) => productItem.advertisement)
                .map((productItem) => productItem.advertisement.ProductId),
            )
          : new Set();
        const sponsoredProductsMatched = matchedAdvertIds.size;
        const sampleCriteoAdvert = criteoAdData?.products?.[0] ?? {};

        logEvent("CriteoSponsoredProductsMatch", {
          productId: String(productId),
          responseId: responseId ?? "",
          sponsoredProductsReturned,
          sponsoredProductsMatched,
          sponsoredProductsUnmatched: Math.max(
            sponsoredProductsReturned - sponsoredProductsMatched,
            0,
          ),
          recommendationIdType: typeof products?.[0]?.productId,
          variantIdCount: variantIds.length,
          sampleCriteoProductId: String(sampleCriteoAdvert?.ProductId ?? ""),
          sampleCriteoParentSku: String(sampleCriteoAdvert?.ParentSKU ?? ""),
        });

        if (productsWithAdverts) {
          safeStateSetter(setMightLikeProductsWithAdverts, productsWithAdverts);
          safeStateSetter(setHasCriteoAds, hasMatchedSponsoredProducts);

          return;
        }

        setAdvertsFallback(products);
      } catch (error) {
        logError(ERROR_TYPE.NonCriticalAppError, {
          message: error.message,
          action: "useRecommendationsCall",
        });
        safeStateSetter(setApiFailed, true);
      }
    };

    fetchRecommendations();

    return () => {
      isCancelled = true;
    };
  }, [
    hasCriteoAdverts,
    advertsSdkInstance,
    areFeaturesReady,
    isMixAndMatch,
    isStockStateReady,
    isInStock,
    productId,
    productPrice,
    isDiscontinued,
    isUnavailableForUS,
    scope,
    pageArea,
    setBannerData,
  ]);

  useEffect(() => {
    if (productId !== previousProductIdRef.current) {
      setApiFailed(false);
      setHasFetchedRecs(false);
      setHasCriteoAds(false);
      setMightLikeProducts(null);
      setMightLikeProductsWithAdverts(null);
      setRecsRequestId("");
      previousProductIdRef.current = productId;
    }
  }, [productId]);

  return {
    mightLikeProducts,
    recsRequestId,
    hasFetchedRecs,
    apiFailed,
    hasCriteoAds,
    mightLikeProductsWithAdverts,
  };
}
